import half_arrow from '../assets/images/half_arrow.png'

class RouteService {
  constructor(map, polyline, dataGroup, parts, vehicle_category) {
    this.map = map
    this.dataGroup = dataGroup
    this.parts = parts
    this.vehicle_category = vehicle_category
    this.polyline = polyline
    this.arrowOverlays = []
    this.ARROW_SPACING = 200
    this.ARROW_SIZE = 0.0005

    this.onComplete = null
    this.onError = null
    this.onProgress = null
  }

  setCallbacks({ onComplete, onError, onProgress }) {
    this.onComplete = onComplete
    this.onError = onError
    this.onProgress = onProgress
  }

  isValidCoordinate(lat, lng) {
    return (
      lat !== null &&
      lng !== null &&
      !isNaN(lat) &&
      !isNaN(lng) &&
      isFinite(lat) &&
      isFinite(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
    )
  }

  createLatLng(lat, lng) {
    if (!this.isValidCoordinate(lat, lng)) {
      console.warn(`Invalid coordinates detected: lat=${lat}, lng=${lng}`)
      return null
    }
    return new google.maps.LatLng(lat, lng)
  }

  finalizeMapSetup(bounds) {
    this.polyline.setMap(this.map)
    this.map.fitBounds(bounds)
    this.map.setZoom(15)
  }

  async processRoutes() {
    try {
      const bounds = new google.maps.LatLngBounds()
      const totalRoutes = this.dataGroup.length
      let processedRoutes = 0

      if (!Array.isArray(this.parts) || !Array.isArray(this.dataGroup)) {
        throw new Error(
          'Invalid data structure: parts and dataGroup must be arrays'
        )
      }

      await Promise.all(
        this.dataGroup.map(async (group, index) => {
          try {
            await this.processRoute(group, index, bounds)

            processedRoutes++
            if (this.onProgress) {
              this.onProgress({
                processed: processedRoutes,
                total: totalRoutes,
                percentage: (processedRoutes / totalRoutes) * 100,
              })
            }
          } catch (routeError) {
            console.warn(`Error processing route ${index}:`, routeError)
          }
        })
      )

      if (processedRoutes === 0) {
        throw new Error('No valid routes were processed')
      }

      this.finalizeMapSetup(bounds)
      await this.addDirectionArrows()

      if (this.onComplete) {
        this.onComplete({
          success: true,
          polyline: this.polyline,
          bounds: bounds,
        })
      }

      return {
        success: true,
        polyline: this.polyline,
        bounds: bounds,
      }
    } catch (error) {
      this.handleError(error)
      throw error
    }
  }

  async processRoute(group, index, bounds) {
    if (
      !this.parts[index] ||
      !Array.isArray(this.parts[index]) ||
      this.parts[index].length === 0
    ) {
      console.warn(`Invalid or empty parts array for index ${index}`)
      return
    }

    const firstPoint = this.parts[index][0]
    const lastPoint = this.parts[index][this.parts[index].length - 1]

    if (!firstPoint || !lastPoint) {
      console.warn(`Invalid first or last point for route ${index}`)
      return
    }

    const startPoint = this.createLatLng(firstPoint.lat, firstPoint.lng)
    const endPoint = this.createLatLng(lastPoint.lat, lastPoint.lng)

    if (!startPoint || !endPoint) {
      console.warn(`Invalid coordinates for route ${index}`)
      return
    }

    bounds.extend(startPoint)
    bounds.extend(endPoint)

    const path = this.polyline.getPath()
    path.push(startPoint)
    path.push(endPoint)

    const distance = google.maps.geometry.spherical.computeDistanceBetween(
      startPoint,
      endPoint
    )
    this.dataGroup[index].distance = distance
  }

  async addDirectionArrows() {
    if (!this.polyline.getPath().getLength()) {
      console.warn('No valid points in polyline to add arrows')
      return
    }

    const polylineLength = this.polyline.Distance()
    const arrowPromises = []

    try {
      const arrowImage = await this.loadImage(half_arrow)

      for (
        let i = 0;
        i < polylineLength - this.ARROW_SPACING;
        i += this.ARROW_SPACING
      ) {
        const point = this.polyline.GetPointAtDistance(i)
        const nextPoint = this.polyline.GetPointAtDistance(i + 50)

        if (!point || !nextPoint) continue

        arrowPromises.push(
          this.createArrowOverlay(i, arrowImage, point, nextPoint)
        )
      }

      const arrows = await Promise.all(arrowPromises)
      this.arrowOverlays.push(...arrows.filter(Boolean))
    } catch (error) {
      console.error('Error creating direction arrows:', error)
    }
  }

  async createArrowOverlay(distance, arrowImage, point, nextPoint) {
    try {
      const heading = google.maps.geometry.spherical.computeHeading(
        point,
        nextPoint
      )

      const imageBounds = {
        north: point.lat() + this.ARROW_SIZE,
        south: point.lat() - this.ARROW_SIZE,
        east: point.lng() + this.ARROW_SIZE,
        west: point.lng() - this.ARROW_SIZE,
      }

      const rotatedImageUrl = await this.rotateImage(arrowImage, heading - 90)

      const arrow = new google.maps.GroundOverlay(
        rotatedImageUrl,
        imageBounds,
        {
          opacity: 1,
          clickable: false,
        }
      )

      arrow.setMap(this.map)
      return arrow
    } catch (error) {
      console.warn('Error creating arrow overlay:', error)
      return null
    }
  }

  loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = src
    })
  }

  rotateImage(image, angle) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      const padding = 350
      canvas.width = image.width + padding
      canvas.height = image.height + image.width / 2 + padding

      const ctx = canvas.getContext('2d')
      ctx.translate(canvas.width / 2, canvas.height / 2)
      ctx.rotate((angle * Math.PI) / 180)
      ctx.drawImage(image, 0, -image.height - 10)

      resolve(canvas.toDataURL())
    })
  }

  handleError(error) {
    console.error('Route processing error:', error)
    this.loadingBtnStop('playback-map')
  }

  cleanup() {
    if (this.polyline) {
      this.polyline.setMap(null)
    }

    this.arrowOverlays.forEach((arrow) => arrow.setMap(null))
    this.arrowOverlays = []
  }

  handleError(error) {
    console.error('Route processing error:', error)
    if (this.onError) {
      this.onError(error)
    }
  }
}

export default RouteService
